import Titulo from '../../components/tipografia/Titulo';
import React, { useCallback, useState } from 'react';
import styles from './page.module.css';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import Descricao from '../../components/tipografia/Descricao';
import HBox from '../../components/layout/HBox';
import Botao from '../../components/Botao';
import VBox from '../../components/layout/VBox';
import CampoTexto from '../../components/Formulario/CampoTexto';
import CampoUpload from '../../components/Formulario/CampoUpload';
import Icon from '@/components/Icon';
import { mdiFileDocument, mdiFileExcel, mdiFilePdfBox, mdiFileWord, mdiLoading, mdiMagnify, mdiPen, mdiPlus, mdiTrashCan, mdiUpload } from '@mdi/js';
import DataTable, { DataTableHeader } from '../../components/layout/DataTable';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import axios from 'axios';
import BotaoPerigoso from '../../components/BotaoPerigoso';
import { useDebounce } from '../chat/useDebounce';
import { toast } from 'react-toastify';
import Modal from '../../components/layout/Modal';
import { FormBaseConhecimento } from './FormBaseConhecimento';
import Grid, { GridItem } from '@/components/layout/Grid';

type ResponseList = {
  status: number;
  ok?: boolean;
  result: any[];
  total: number;
  cursor: string | number | null;
};

type Props = {};

const numFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'decimal',
  maximumFractionDigits: 2,
});

const Page = ({ }: Props) => {
  const [editObject, setEditObject] = React.useState<any>(null);
  const [fieldErrors, setFieldErrors] = React.useState<Record<
    string,
    string
  > | null>(null);

  const [search, setSearch] = React.useState<string>('');
  const debouncedSearch = useDebounce(search, 500);

  // Estado do modal de upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadTitle, setUploadTitle] = useState<string>('');
  const [csvMinLinhas, setCsvMinLinhas] = useState<number>(100);
  const [csvMaxLinhas, setCsvMaxLinhas] = useState<number>(300);

  // Estado do job assíncrono
  const [uploadJobId, setUploadJobId] = useState<number | null>(null);
  const [uploadJobStatus, setUploadJobStatus] = useState<any>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Detecta se o arquivo é CSV/XLSX para mostrar opções de chunking
  const hasSpreadsheetFile = uploadFiles.some(f =>
    f.name.toLowerCase().endsWith('.csv') ||
    f.name.toLowerCase().endsWith('.xlsx') ||
    f.name.toLowerCase().endsWith('.xls')
  );

  // Retorna ícone baseado no tipo de arquivo
  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return mdiFilePdfBox;
      case 'doc':
      case 'docx': return mdiFileWord;
      case 'csv':
      case 'xls':
      case 'xlsx': return mdiFileExcel;
      default: return mdiFileDocument;
    }
  };

  const headers: DataTableHeader[] = [
    { label: 'Título', width: '40%', column: 'titulo' },
    {
      label: 'Tipo',
      width: '100px',
      render: (row: any) => {
        const tipo = row.tipo;

        // Documento ainda sendo processado
        if (tipo === null || tipo === undefined) {
          return (
            <HBox gap="4px" style={{ alignItems: 'center' }}>
              <Icon path={mdiLoading} size={1} color="var(--tc-color-gray-600)" spin />
              <span style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-600)' }}>Processando...</span>
            </HBox>
          );
        }

        const config: Record<string, { icon: string; color: string; label: string }> = {
          pdf: { icon: mdiFilePdfBox, color: 'var(--tc-color-danger)', label: 'PDF' },
          docx: { icon: mdiFileWord, color: 'var(--tc-color-info)', label: 'Word' },
          csv: { icon: mdiFileExcel, color: 'var(--tc-color-success)', label: 'CSV' },
          xlsx: { icon: mdiFileExcel, color: 'var(--tc-color-success)', label: 'Excel' },
          texto: { icon: mdiFileDocument, color: 'var(--tc-color-gray-600)', label: 'Texto' },
          markdown: { icon: mdiFileDocument, color: 'var(--tc-color-gray-600)', label: 'MD' },
        };
        const c = config[tipo] || config.texto;
        return (
          <HBox gap="4px" style={{ alignItems: 'center' }}>
            <Icon path={c.icon} size={1} color={c.color} />
            <span style={{ fontSize: '0.85rem' }}>{c.label}</span>
          </HBox>
        );
      },
    },
    {
      label: 'Seções',
      width: '80px',
      render: rows => {
        return rows.secoes?.length ?? '--';
      },
    },
    {
      label: 'Ações',
      width: '100px',
      stickRight: true,
      alignRight: true,
      render: (row: any) => {
        return (
          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            <Botao
              title="Editar"
              variant="none-primary"
              icon={mdiPen}
              onClick={() => setEditObject(row)}
            />
            <BotaoPerigoso
              buttonProps={{ title: 'Apagar' }}
              variant="none-danger"
              icon={mdiTrashCan}
              onClick={() => handleDelete(row.id)}
            />
          </HBox>
        );
      },
    },
  ];

  const headersSemantic: DataTableHeader[] = [
    {
      label: 'Pontuação',
      width: '50px',
      render: (row): React.ReactNode => {
        return numFormatter.format(row.relevancia_combinada);
      },
    },
    {
      label: 'Título',
      width: '50%',
      column: 'titulo',
    },
    {
      label: 'Subtítulo',
      width: '50%',
      column: 'secao_titulo',
    },
  ];

  const queryList = useInfiniteQuery({
    queryKey: ['base_conhecimento', debouncedSearch],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        txt: debouncedSearch,
        c: pageParam,
      });

      return await axios.get<ResponseList>(
        `/api/base_conhecimento?${params.toString()}`,
        {
          withCredentials: true,
        },
      );
    },
    getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    refetchInterval: (data) => {
      // Verifica se há documentos em processamento (tipo === null)
      if (!data?.pages) return false;

      const hasProcessing = data.pages.some(page =>
        page?.data?.result?.some((doc: any) => doc.tipo === null)
      );
      // Atualiza a cada 3 segundos se houver documentos processando
      return hasProcessing ? 3000 : false;
    },
  });

  const changeMutator = useMutation(
    async () => {
      if (!editObject) return;

      if (editObject.secoes) {
        for (let i = 0; i < editObject.secoes?.length; i++) {
          const s = editObject.secoes[i];
          s.indice = i;
        }
      }

      setFieldErrors(null);
      if (editObject.id) {
        return await axios.put(
          `/api/base_conhecimento/${editObject.id}`,
          editObject,
          {
            withCredentials: true,
          },
        );
      } else {
        return await axios.post(`/api/base_conhecimento`, editObject, {
          withCredentials: true,
        });
      }
    },
    {
      onSuccess: () => {
        setFieldErrors(null);
        queryList.refetch();
        setEditObject(null);

        if (!editObject.id) {
          toast('Documento adicionado com sucesso', {
            toastId: 'add-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        } else {
          toast('Documento alterado com sucesso', {
            toastId: 'edit-success',
            position: 'top-center',
            type: 'success',
            autoClose: 5000,
          });
        }
      },
      onError: (err: any) => {
        if (err.response?.status === 422) {
          setFieldErrors(err.response?.data?.fieldErrors ?? {});
        }
        toast(err.response?.data?.msg ?? 'Erro desconhecido', {
          toastId: 'add-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
    },
  );

  const deleteMutator = useMutation(
    async (id: number) => {
      return await axios.delete(`/api/base_conhecimento/${id}`, {
        withCredentials: true,
      });
    },
    {
      onSuccess: () => {
        toast('Documento apagado com sucesso', {
          toastId: 'del-success',
          position: 'top-center',
          type: 'success',
          autoClose: 5000,
        });
        queryList.refetch();
      },
      onError: (err: any) => {
        console.error(err);
        toast('Ocorreu um erro ao tentar apagar o documento.', {
          toastId: 'del-error',
          position: 'top-center',
          type: 'warning',
          autoClose: 5000,
        });
      },
      onSettled: () => {
        queryList.refetch();
      },
    },
  );

  const handleChange = useCallback(async () => {
    await changeMutator.mutate();
  }, [changeMutator]);

  const handleDelete = (id: number) => {
    deleteMutator.mutate(id);
  };

  const handleCloseModal = () => {
    setEditObject(null);
    setFieldErrors(null);
  };

  const handleOpenModalNewObject = () => {
    setEditObject({
      secoes: [{ titulo: '', conteudo: '' }],
    });
  };

  // Polling para verificar status do job
  React.useEffect(() => {
    if (!uploadJobId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/base_conhecimento/upload/status/${uploadJobId}`);
        const jobData = response.data.job;
        setUploadJobStatus(jobData);

        // Verifica se o job terminou
        if (jobData.status === 'concluido') {
          clearInterval(pollInterval);
          setIsPolling(false);

          // Fecha o toast de processamento
          toast.dismiss('upload-processing');

          // Atualiza a lista
          queryList.refetch();

          // Fecha o modal
          setShowUploadModal(false);
          setUploadFiles([]);
          setUploadTitle('');
          setCsvMinLinhas(100);
          setCsvMaxLinhas(300);
          setUploadJobId(null);
          setUploadJobStatus(null);

          // Notifica sucesso
          const { documentos_criados, erros, total_arquivos } = jobData;
          const successCount = documentos_criados?.length || 0;
          const errorCount = erros?.length || 0;

          if (errorCount > 0 && successCount > 0) {
            toast(`✅ ${successCount} documento(s) processado(s). ⚠️ ${errorCount} com erro.`, {
              toastId: 'upload-partial',
              position: 'top-center',
              type: 'warning',
              autoClose: 7000,
            });
          } else if (errorCount > 0) {
            const errorMsgs = erros?.slice(0, 2).map((e: any) => `${e.filename}: ${e.error}`).join(', ');
            toast(`❌ Erro ao processar: ${errorMsgs}${erros.length > 2 ? '...' : ''}`, {
              toastId: 'upload-error',
              position: 'top-center',
              type: 'error',
              autoClose: 7000,
            });
          } else {
            toast(`✅ ${successCount} documento(s) processado(s) com sucesso!`, {
              toastId: 'upload-success',
              position: 'top-center',
              type: 'success',
              autoClose: 5000,
            });
          }
        } else if (jobData.status === 'erro') {
          clearInterval(pollInterval);
          setIsPolling(false);

          // Fecha o toast de processamento
          toast.dismiss('upload-processing');

          toast(`❌ Erro ao processar documentos: ${jobData.erro_mensagem || 'Erro desconhecido'}`, {
            toastId: 'upload-error',
            position: 'top-center',
            type: 'error',
            autoClose: 7000,
          });
        }
      } catch (error) {
        console.error('Erro ao consultar status do job:', error);
      }
    }, 2000); // Poll a cada 2 segundos

    return () => clearInterval(pollInterval);
  }, [uploadJobId, isPolling, queryList]);

  // Mutation para upload de arquivos (agora assíncrono)
  const uploadMutator = useMutation(
    async () => {
      if (uploadFiles.length === 0) return;

      const formData = new FormData();

      // Se for apenas um arquivo, usa campo 'arquivo'
      if (uploadFiles.length === 1) {
        formData.append('arquivo', uploadFiles[0]);

        if (uploadTitle.trim()) {
          formData.append('titulo', uploadTitle.trim());
        }
      } else {
        // Múltiplos arquivos - usa campo 'files[]'
        for (const file of uploadFiles) {
          formData.append('files[]', file);
        }
      }

      // Adiciona config de chunking para CSV/XLSX
      if (hasSpreadsheetFile) {
        formData.append('csvMinLinhas', csvMinLinhas.toString());
        formData.append('csvMaxLinhas', csvMaxLinhas.toString());
      }

      // Endpoint agora retorna job_id imediatamente
      return await axios.post('/api/base_conhecimento/upload', formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: (response) => {
        // Agora recebe job_id e inicia polling
        const jobData = response?.data?.job;

        if (jobData?.id) {
          setUploadJobId(jobData.id);
          setUploadJobStatus(jobData);
          setIsPolling(true);

          toast(`Processando ${jobData.total_arquivos} arquivo(s)...`, {
            toastId: 'upload-processing',
            position: 'top-center',
            type: 'info',
            autoClose: false,
          });
        }
      },
      onError: (err: any) => {
        toast(err.response?.data?.msg ?? 'Erro ao iniciar upload', {
          toastId: 'upload-error',
          position: 'top-center',
          type: 'error',
          autoClose: 5000,
        });
      },
    },
  );

  const handleUpload = useCallback(async () => {
    await uploadMutator.mutateAsync();
  }, [uploadMutator]);

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setUploadTitle('');
    setCsvMinLinhas(100);
    setCsvMaxLinhas(300);
  };

  const queryFlatList =
    queryList.data?.pages?.map(p => p.data.result)?.flat() ?? [];
  const total =
    queryList.data?.pages?.[queryList.data.pages.length - 1]?.data?.total ?? 0;
  const editMode = !!editObject?.id && editObject.id > 0;

  const [showSemanticSearch, setShowSemanticSearch] = React.useState(false);
  const [semanticSearch, setSemanticSearch] = React.useState<string>('');

  const debouncedSemanticSearch = useDebounce(semanticSearch, 500);

  const qSemantic = useQuery({
    queryKey: ['base_conhecimento_semantic', debouncedSemanticSearch],
    queryFn: async () => {
      return await axios.get(
        `/api/base_conhecimento/busca?txt=${semanticSearch}`,
      );
    },
    enabled: debouncedSemanticSearch?.length > 0,
  });

  return (
    <LayoutUsuario>
      <Modal
        instanceId='modal-busca-semantica'
        size="md"
        title="Busca semântica"
        show={showSemanticSearch}
        onClose={() => setShowSemanticSearch(false)}
        onCancel={() => setShowSemanticSearch(false)}
        confirmProps={{
          visible: false,
        }}
        cancelProps={{
          label: 'Fechar',
        }}
      >
        <VBox>
          <CampoTexto
            placeholder="Buscar por"
            prepend={
              <Icon
                path={mdiMagnify}
                size={1}
                color="var(--tc-color-gray-500)"
              />
            }
            value={semanticSearch}
            onChange={setSemanticSearch}
          />
          {qSemantic.isLoading && (
            <div>
              Aguarde...{' '}
              <Icon
                path={mdiLoading}
                size={.8}
                color="var(--tc-color-gray-700)"
                spin
              />
            </div>
          )}
          {!qSemantic.isLoading && (
            <div>
              Exibindo {qSemantic.data?.data?.result?.length ?? '0'} de{' '}
              {qSemantic.data?.data?.total ?? '0'} resultados.
            </div>
          )}
          <DataTable
            isLoading={qSemantic.isLoading}
            cabecalhos={headersSemantic}
            linhas={qSemantic.data?.data?.result ?? []}
            hasMoreData={false}
            onLoadMore={() => { }}
          />
        </VBox>
      </Modal>

      <Modal
        instanceId='modal-documento'
        size="lg"
        ignoreOverlayClick
        title={editMode ? 'Editar documento' : 'Novo documento'}
        show={!!editObject}
        onClose={handleCloseModal}
        onCancel={handleCloseModal}
        onConfirm={handleChange}
        confirmProps={{
          disabled: changeMutator.isLoading,
        }}
        cancelProps={{
          disabled: changeMutator.isLoading,
        }}
      >
        <FormBaseConhecimento
          value={editObject as any}
          onChange={setEditObject}
          fieldErrors={fieldErrors}
        />
      </Modal>

      {/* Modal de Upload */}
      <Modal
        instanceId='modal-upload'
        size="md"
        ignoreOverlayClick
        title="Importar Documento(s)"
        show={showUploadModal}
        onClose={handleCloseUploadModal}
        onCancel={handleCloseUploadModal}
        onConfirm={handleUpload}
        confirmProps={{
          label: uploadMutator.isLoading ? 'Importando...' : (uploadFiles.length > 1 ? `Importar ${uploadFiles.length} arquivos` : 'Importar'),
          disabled: uploadMutator.isLoading || uploadFiles.length === 0,
        }}
        cancelProps={{
          disabled: uploadMutator.isLoading,
        }}
      >
        <VBox gap="16px">
          <Descricao>
            Faça upload de documentos para a base de conhecimento. Formatos suportados:
          </Descricao>

          <HBox style={{}}>
            <HBox gap="4px" style={{ alignItems: 'center', flex: '0 1 0' }} title="PDF">
              <Icon path={mdiFilePdfBox} size={1.5} color="var(--tc-color-danger)" />
              {/* <span style={{ fontSize: '0.85rem' }}>PDF</span> */}
            </HBox>
            <HBox gap="4px" style={{ alignItems: 'center', flex: '0 1 0' }} title="Word">
              <Icon path={mdiFileWord} size={1.5} color="var(--tc-color-info)" />
              {/* <span style={{ fontSize: '0.85rem' }}>Word</span> */}
            </HBox>
            <HBox gap="4px" style={{ alignItems: 'center', flex: '0 1 0' }} title="CSV/Excel">
              <Icon path={mdiFileExcel} size={1.5} color="var(--tc-color-success)" />
              {/* <span style={{ fontSize: '0.85rem' }}>CSV/Excel</span> */}
            </HBox>
            <HBox gap="4px" style={{ alignItems: 'center', flex: '0 1 0' }} title="TXT/Markdown">
              <Icon path={mdiFileDocument} size={1.5} color="var(--tc-color-gray-600)" />
              {/* <span style={{ fontSize: '0.85rem' }}>TXT/Markdown</span> */}
            </HBox>
          </HBox>

          <CampoUpload
            instanceId="upload-documento"
            label="Arquivos"
            value={uploadFiles}
            onChange={setUploadFiles}
            accept=".pdf,.docx,.doc,.csv,.xlsx,.xls,.md,.txt,.markdown"
            maxFiles={10}
            maxSize={25 * 1024 * 1024}
            placeholder="Arraste arquivos aqui ou clique para selecionar (máx. 10 arquivos, 25MB cada)"
          />

          {uploadFiles.length === 1 && (
            <CampoTexto
              label="Título (opcional)"
              placeholder="Deixe vazio para extrair automaticamente do documento"
              value={uploadTitle}
              onChange={setUploadTitle}
              tooltipText="Se não informado, o título será extraído do nome do arquivo ou do conteúdo."
            />
          )}

          {uploadFiles.length > 1 && (
            <Descricao style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-600)' }}>
              {uploadFiles.length} arquivo(s) selecionado(s). Os títulos serão extraídos automaticamente de cada documento.
            </Descricao>
          )}

          {(uploadMutator.isLoading || isPolling) && (
            <VBox gap="8px">
              <HBox gap="8px" style={{ alignItems: 'center' }}>
                <Icon
                  path={mdiLoading}
                  size={1}
                  color="var(--tc-color-primary)"
                  spin
                />
                <span>
                  {uploadMutator.isLoading
                    ? 'Iniciando upload...'
                    : `Processando... ${uploadJobStatus?.progresso || 0}%`
                  }
                </span>
              </HBox>
              {isPolling && uploadJobStatus && (
                <div style={{
                  width: '100%',
                  height: '4px',
                  backgroundColor: 'var(--tc-color-gray-200)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${uploadJobStatus.progresso || 0}%`,
                    height: '100%',
                    backgroundColor: 'var(--tc-color-primary)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              )}
              {isPolling && uploadJobStatus && (
                <Descricao style={{ fontSize: '0.8rem', color: 'var(--tc-color-gray-600)' }}>
                  {uploadJobStatus.arquivos_processados || 0} de {uploadJobStatus.total_arquivos || 0} arquivo(s) processado(s)
                </Descricao>
              )}
            </VBox>
          )}
        </VBox>
      </Modal>

      <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
        {/* Header */}
        <Grid>
          <GridItem sm={12} md={6}>
            <VBox>
              <Titulo>Documentos (Base de Conhecimento)</Titulo>
              <Descricao>
                Estes documentos irão ajudar seus atendentes e os agentes de IA a
                entenderem melhor seu negócio e seus processos internos.
              </Descricao>
            </VBox>
          </GridItem>
          <GridItem sm={12} md={6}>
            <HBox gap="12px" className={styles.headerAcoes}>
              <Botao
                variant="outline-primary"
                icon={mdiMagnify}
                onClick={() => setShowSemanticSearch(true)}
              >
                Busca semântica
              </Botao>
              <Botao
                variant="outline-primary"
                icon={mdiUpload}
                onClick={() => setShowUploadModal(true)}
              >
                Importar
              </Botao>
              <Botao
                variant="primary"
                icon={mdiPlus}
                onClick={handleOpenModalNewObject}
              >
                Novo
              </Botao>
            </HBox>
          </GridItem>
        </Grid>

        {/* Filtros */}
        <HBox>
          <CampoTexto
            prepend={
              <Icon
                path={mdiMagnify}
                size={1}
                color="var(--tc-color-gray-500)"
              />
            }
            placeholder="Pesquisar"
            value={search}
            onChange={setSearch}
          />
        </HBox>

        {queryList.isFetching && (
          <div>
            Aguarde...{' '}
            <Icon
              path={mdiLoading}
              size={.8}
              color="var(--tc-color-gray-700)"
              spin
            />
          </div>
        )}
        {!queryList.isFetching && (
          <div>
            Exibindo {queryFlatList?.length ?? '0'} de {total ?? '0'}{' '}
            resultados.
          </div>
        )}

        <DataTable
          isLoading={queryList.isFetching}
          cabecalhos={headers}
          linhas={queryFlatList ?? []}
          hasMoreData={queryList.hasNextPage}
          onLoadMore={async () => queryList.fetchNextPage()}
        />
      </VBox>
    </LayoutUsuario>
  );
};

export default Page;
